import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import {
  getCustomFields,
  deleteCustomField,
  reorderCustomFields,
} from '../../services/customFieldService';
import { getFieldTypeLabel } from '../../utils/customFieldUtils';
import ConfirmDialog from '../ConfirmDialog';
import ReorderListModal from '../ReorderListModal';
import CustomFieldModal, { CUSTOM_FIELD_ENTITIES as ENTITIES } from './CustomFieldModal';

export default function CustomFieldManager({ onMessage }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderEntity, setReorderEntity] = useState('lead');
  const [savingOrder, setSavingOrder] = useState(false);

  const loadFields = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCustomFields();
      setFields(data.fields || []);
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to load custom fields';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const openCreateModal = () => {
    setEditingField(null);
    setShowModal(true);
  };

  const openEditModal = (field) => {
    setEditingField(field);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingField(null);
  };

  const handleDelete = async (field) => {
    if (deleting === field.field_id) return;
    setDeleting(field.field_id);
    try {
      const result = await deleteCustomField(field.field_id);
      onMessage({
        message: result?.archived
          ? `"${field.name}" removed. Existing values are kept in case you add it back.`
          : `"${field.name}" deleted`,
        type: 'success',
      });
      await loadFields();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to delete field';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  // Order is kept per entity, so the reorder list only ever shows one entity's
  // fields and saves that entity's order.
  const reorderItems = fields.filter((f) => (f.applies_to || []).includes(reorderEntity));

  const openReorder = (entity) => {
    setReorderEntity(entity);
    setShowReorderModal(true);
  };

  const handleSaveReorder = async (orderedFieldIds) => {
    try {
      setSavingOrder(true);
      await reorderCustomFields(reorderEntity, orderedFieldIds);
      setShowReorderModal(false);
      onMessage({ message: 'Field order updated', type: 'success' });
      await loadFields();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to reorder fields';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading custom fields...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Custom Fields
          </h3>
          <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
            Add your own properties to leads, projects, people, resources, companies and contacts. They appear
            as table columns and on the detail panel. A field applied to both leads and
            projects carries its value across when a lead is accepted.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {ENTITIES.map(({ value, label }) => (
            fields.filter((f) => (f.applies_to || []).includes(value)).length > 1 && (
              <button
                key={value}
                className={styles.buttonSecondary}
                onClick={() => openReorder(value)}
              >
                Reorder {label}
              </button>
            )
          ))}
          <button className={styles.buttonPrimary} onClick={openCreateModal}>
            + New Field
          </button>
        </div>
      </div>

      {fields.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No custom fields yet. Add one to start tracking your own properties.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {fields.map((field) => (
            <div key={field.field_id} className={settingsStyles.labelCard}>
              <div className={settingsStyles.labelInfo}>
                <span
                  className={settingsStyles.labelBadge}
                  style={{ backgroundColor: '#e2e8f0', color: '#1e293b' }}
                >
                  {field.name}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {getFieldTypeLabel(field.field_type)}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {(field.applies_to || [])
                    .map((e) => ENTITIES.find((x) => x.value === e)?.label || e)
                    .join(' · ')}
                </span>
                {field.description && (
                  <span className={settingsStyles.labelDescription}>
                    {field.description}
                  </span>
                )}
              </div>
              <div className={settingsStyles.roleActions}>
                <button
                  className={styles.buttonSecondary}
                  onClick={() => openEditModal(field)}
                >
                  Edit
                </button>
                <button
                  className={styles.buttonDanger}
                  onClick={() => setConfirmDelete(field)}
                  disabled={deleting === field.field_id}
                >
                  {deleting === field.field_id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <CustomFieldModal
          field={editingField}
          onClose={closeModal}
          onSaved={loadFields}
          onMessage={onMessage}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Remove the "${confirmDelete.name}" field? It disappears from tables and detail panels. Any values already recorded are kept, so adding the field back restores them.`}
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <ReorderListModal
        isOpen={showReorderModal}
        title={`Reorder ${ENTITIES.find((e) => e.value === reorderEntity)?.label} Fields`}
        description="Drag fields to change the order of their columns and detail-panel rows."
        items={reorderItems}
        getId={(item) => item.field_id}
        getLabel={(item) => item.name}
        onClose={() => setShowReorderModal(false)}
        onSave={handleSaveReorder}
        saving={savingOrder}
      />
    </div>
  );
}
